import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Filter Email Step
 *
 * A generic, reusable step that filters inbound emails based on configurable
 * pattern matching against sender, subject, and body fields.
 *
 * Each field supports an array of patterns that can be:
 * - Plain text (case-insensitive substring match)
 * - Regex (detected by \b, ^, $, or other metacharacters)
 *
 * All configured field groups use AND logic (sender AND subject AND body must match).
 * Within each field group, patterns use OR logic (any pattern in the group can match).
 * Empty/omitted field groups are treated as "match all" for that field.
 *
 * Use this step at the start of a workflow to validate that an email matches
 * expected patterns before proceeding with processing.
 */
export const filterEmailStep = defineStepConfig({
  type: "filter_email",
  name: "Filter Email",
  description:
    "Filter inbound emails by matching sender, subject, and body patterns. Passes through emails that match all configured criteria.",
  category: "Transformation",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email to filter",
    ),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email",
    ),
    matched: z
      .boolean()
      .describe("Whether the email matched all configured filter criteria"),
    confidence: z
      .number()
      .min(0)
      .max(1)
      .describe("Confidence score based on which fields matched (0-1)"),
    matchDetails: z
      .string()
      .describe(
        "Human-readable summary of which patterns matched or why the email was filtered out",
      ),
  }),

  configSchema: z.object({
    excludedSenders: z
      .array(z.string())
      .optional()
      .describe(
        "Exact sender email addresses to block. When configured, emails from these senders will not match.",
      ),
    senderPatterns: z
      .array(z.string())
      .optional()
      .describe(
        "Patterns to match against sender email address. Case-insensitive substring or regex. OR logic within group.",
      ),
    subjectPatterns: z
      .array(z.string())
      .optional()
      .describe(
        "Patterns to match against email subject. Case-insensitive substring or regex. OR logic within group.",
      ),
    bodyPatterns: z
      .array(z.string())
      .optional()
      .describe(
        "Patterns to match against email body (text or HTML). Case-insensitive substring or regex. OR logic within group.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 2,
    },
  },
});
